
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bike } from '@/services/types';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';

interface BikeDetailsDialogProps {
  bikeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BikeDetailsDialog({ bikeId, open, onOpenChange }: BikeDetailsDialogProps) {
  const [bike, setBike] = useState<Bike | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open && bikeId) {
      fetchBikeDetails(bikeId);
    }
  }, [open, bikeId]);

  const fetchBikeDetails = async (bikeId: string) => {
    try {
      setLoading(true);
      
      const { data: bikeData, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('id', bikeId)
        .single();
      
      if (error) {
        throw new Error(`Ошибка при получении данных: ${error.message}`);
      }
      
      if (bikeData) {
        // Преобразуем данные из БД в формат, соответствующий нашему типу Bike
        const mappedBike: Bike = {
          id: bikeData.id,
          serialNumber: bikeData.serial_number || 'Н/Д',
          model: bikeData.model || 'Неизвестная модель',
          status: (bikeData.status || 'available') as 'available' | 'in_use' | 'maintenance',
          lastMaintenance: bikeData.last_service_date || new Date().toISOString(),
          gps_tag: bikeData.gps_tag || 'Н/Д',
          engine_hours: bikeData.engine_hours || '0',
          mileage: String(bikeData.mileage || 0) // Конвертируем в строку
        };
        
        setBike(mappedBike);
      }
    } catch (error: any) {
      toast.error(`Не удалось загрузить данные велосипеда: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Информация о велосипеде
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Загрузка данных...</span>
          </div>
        ) : bike ? (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Модель</p>
                  <p className="font-medium">{bike.model}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Серийный номер</p>
                  <p className="font-medium">{bike.serialNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Статус</p>
                  <StatusBadge status={bike.status} type="bike" />
                </div>
                <div>
                  <Label htmlFor="mileage">Пробег (км)</Label>
                  <Input
                    id="mileage"
                    name="mileage"
                    type="text"
                    defaultValue={bike.mileage || '0'}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">GPS метка</p>
                  <p className="font-medium">{bike.gps_tag || 'Не указана'}</p>
                </div>
                <div>
                  <Label htmlFor="engine_hours">Моточасы</Label>
                  <Input
                    id="engine_hours"
                    name="engine_hours"
                    type="text"
                    defaultValue={bike.engine_hours || ''}
                  />
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-muted-foreground">Последнее ТО</p>
                  <p className="font-medium">{new Date(bike.lastMaintenance).toLocaleDateString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center p-8 text-muted-foreground">
            Данные о велосипеде не найдены
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
